import React, { ComponentPropsWithoutRef, useState } from 'react';
import { toast } from 'react-toastify';

import { SendTransactionData } from '@wagmi/core/src/query/sendTransaction';

import classNames from 'classnames';
import { BigNumber } from 'ethers';
import { capitalize } from 'lodash';
import { useSendTransaction } from 'wagmi';

import {
  useGetOneInchTokenAllowanceQuery,
  useIncreaseOneInchTokenAllowanceMutation,
  useStoreSwapTxMutation,
  useSwapTokensMutation,
} from '@query';

import { OneInchNetwork, OneInchToken } from '@api';

import { Spinner } from '@uikit/components/Spinner';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import NextLink from '@shared/common/components/NextLink';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { namedErrors, namedWeb3Errors } from '@shared/constants';
import { nextProjectUrl } from '@shared/constants/urls';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { numberToWei } from '@shared/helpers/web3/formatters/numberToWei';
import { handleTransactionResult } from '@shared/helpers/web3/handleTransactionResult';
import useWeb3 from '@shared/helpers/web3/hooks/useWeb3';
import launchAsyncToast from '@shared/helpers/web3/launchAsyncToast';
import truncate from '@shared/helpers/web3/truncate';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';
import useMagicIdWallet from '@shared/hooks/useMagicIdWallet';

import useSwitchNetworkUtil from '@magic-swap/components/pages/MagicSwap/hooks/useSwitchNetworkUtil';
import { OneInchView } from '@magic-swap/components/pages/MagicSwap/types';
import { SuccessSwapTxInfo } from '@magic-swap/components/pages/MagicSwap/ui/views/MagicSwapSuccessView';
import getVerifiedMagicSwapNetworkId from '@magic-swap/components/pages/MagicSwap/utils/getVerifiedMagicSwapNetworkId';

interface MagicSwapButtonProps extends ComponentPropsWithoutRef<'button'> {
  network?: OneInchNetwork;
  fromToken?: OneInchToken;
  toToken?: OneInchToken;
  amount: number;
  slippage: number;
  balanceExceeded: boolean;
  allowanceQuery: ReturnType<typeof useGetOneInchTokenAllowanceQuery>;
  isLoading?: boolean;
  setView: (view: OneInchView) => void;
  setSuccessTxInfo: (tx: SuccessSwapTxInfo) => void;
  setTxError: (e: Error) => void;
}

const MagicSwapButton = ({
  allowanceQuery,
  network,
  fromToken,
  setView,
  toToken,
  amount,
  isLoading: externalLoading,
  slippage,
  balanceExceeded,
  setSuccessTxInfo,
  setTxError,
}: MagicSwapButtonProps) => {
  const { user } = useAuth();
  const { track } = useMixPanel();

  const trackGivePermissionButtonClick = () => {
    if (user?.id) {
      track(MIXPANEL_EVENT_NAMES.SWAP_GIVE_PERMISSION_BUTTON_CLICK, {
        distinct_id: user.id,
      });
    }
  };

  const trackSwapButtonClick = () => {
    if (user?.id) {
      track(MIXPANEL_EVENT_NAMES.SWAP_BUTTON_CLICK, {
        distinct_id: user.id,
      });
    }
  };

  const authModal = useAuthModal();
  const { userWallet, isAuthed, isLoadingUser } = useMagicIdWallet();
  const { getUserWalletType } = useMagicIdUserWalletInfo();

  const { connectWallet, web3Account } = useWeb3();
  const { sendTransactionAsync } = useSendTransaction();
  const { switchNetworkUtil } = useSwitchNetworkUtil();

  const { data: allowance, refetch: fetchAllowance } = allowanceQuery;
  const { mutateAsync: increaseAllowance } = useIncreaseOneInchTokenAllowanceMutation();
  const { mutateAsync: swapTokens } = useSwapTokensMutation();
  const { mutateAsync: storeSwapTx } = useStoreSwapTxMutation();

  const [internalLoading, setInternalLoading] = useState(false);

  const { address = '' } = web3Account;
  const isLoading = externalLoading || internalLoading;
  const userAddress = userWallet?.attributes?.address || '';
  const isConnectedToSelfWallet = address.toLowerCase() === userAddress.toLowerCase();

  const handleConnect = async () => {
    if (!userWallet?.attributes.address) {
      const text = 'Please add a EVM wallet in your Magic ID';
      return toast.info(text);
    }

    if (!address) {
      try {
        if (!network) {
          throw new Error('Please provide a network');
        }

        const verifiedNetworkId = getVerifiedMagicSwapNetworkId(network);
        const { walletType } = getUserWalletType();

        const connectedAddress = await connectWallet({
          walletType: walletType,
          targetAddress: userWallet.attributes.address,
          disabledCache: true,
          chainId: verifiedNetworkId,
        });

        await switchNetworkUtil({ networkId: network.id });

        const isSelfWalletConnected =
          connectedAddress?.toLowerCase() === userWallet?.attributes?.address?.toLowerCase();

        if (isSelfWalletConnected) {
          fetchAllowance().then();
        }
      } catch (error) {
        if (normalizeError(error).message !== namedWeb3Errors.PLEASE_CONNECT_TO_PROCEED) {
          displayWalletErrorToast({ error });
        }
      }
    }
  };

  const isEnoughAllowance = Boolean(
    allowance?.data?.allowance &&
      amount &&
      fromToken &&
      numberToWei(amount, fromToken?.attributes.decimal).lte(
        BigNumber.from(allowance?.data?.allowance ?? 0),
      ),
  );

  const handleIncreaseAllowance = async () => {
    if (!fromToken || !network || !amount) {
      return;
    }

    const tx = await increaseAllowance({
      networkId: network?.id,
      options: {
        searchParams: {
          tokenAddress: fromToken?.id,
          amount: numberToWei(amount, fromToken.attributes.decimal) as any,
        },
      },
    });

    const hash = await sendTransactionAsync({
      ...(tx.data as any),
    });

    if (!hash) {
      return;
    }

    await handleTransactionResult(hash);
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const handlePermissionCheck = async (props?: { skipAllowanceCheck?: boolean }) => {
    trackGivePermissionButtonClick();

    const { skipAllowanceCheck = false } = props ?? {};

    if (!fromToken || !network || !address) {
      return;
    }

    try {
      setInternalLoading(true);

      if (!skipAllowanceCheck) {
        const { data: allowanceResponse } = await fetchAllowance();

        if (allowanceResponse && 'error' in allowanceResponse && 'message' in allowanceResponse) {
          throw new Error(allowanceResponse.message as any);
        }

        const isEnough = Boolean(
          allowanceResponse?.data?.allowance &&
            numberToWei(amount, fromToken?.attributes.decimal).lte(
              BigNumber.from(allowanceResponse?.data?.allowance),
            ),
        );

        if (isEnough) {
          setInternalLoading(false);
          return;
        }
      }

      await handleIncreaseAllowance();

      return handlePermissionCheck();
    } catch (error) {
      const normalizedError = normalizeError(error);

      if (normalizedError.message.includes('Not enough allowance')) {
        return handlePermissionCheck({ skipAllowanceCheck: true });
      }

      setInternalLoading(false);
      displayWalletErrorToast({ error });
    }
  };

  const isAbleToSwap =
    !isLoading &&
    isEnoughAllowance &&
    network &&
    fromToken &&
    toToken &&
    address &&
    !balanceExceeded;

  const handleSwap = async () => {
    trackSwapButtonClick();

    if (!isAbleToSwap || !fromToken || !toToken) {
      return;
    }

    const toastId = Date.now();

    try {
      setInternalLoading(true);

      const response = await swapTokens({
        networkId: network?.id,
        options: {
          searchParams: {
            fromTokenAddress: fromToken?.id,
            toTokenAddress: toToken?.id,
            amount: numberToWei(amount, fromToken.attributes.decimal) as any,
            walletAddress: address,
            slippage,
          },
        },
      });

      if ('error' in response) {
        throw new Error(capitalize(response?.message || response.error?.message || ''));
      }

      const operationId = response.id;
      const { walletType, userWallet: userWalletInfo } = getUserWalletType();

      const hash = (await Promise.race([
        sendTransactionAsync({
          ...(response.data.tx as any),
        }),
        launchAsyncToast({
          walletType,
          address: userWalletInfo.attributes.address,
          toastId,
          showExtraInfo: true,
          hideRetryButton: true,
          title: `Swapping ${fromToken.attributes.symbol} to ${toToken.attributes.symbol}...`,
          // description: 'Please be careful with Retry button to not pay more than 1 time',
        }),
      ])) as Awaited<SendTransactionData>;

      if (!hash) {
        toast.dismiss(toastId);
        return;
      }

      const chain = web3Account.chain;
      const receipt = await handleTransactionResult(hash, chain);
      const chainScanLink = receipt?.linkToBlockExplorer ?? '';

      await storeSwapTx({
        networkId: network?.id,
        options: {
          json: {
            tx: hash,
            operationId,
          },
        },
      });

      setSuccessTxInfo({
        chainScanLink,
      });
      setView('success');
      toast.dismiss(toastId);
    } catch (error) {
      toast.dismiss(toastId);

      const message = displayWalletErrorToast({ error, justReturnHandledMessage: true });

      setTxError(
        normalizeError(
          new Error(
            typeof message === 'string' ? message : namedErrors.DEFAULT_RETRY_OR_CONTACT_SUPPORT,
          ),
        ),
      );
      setView('failure');
    } finally {
      setInternalLoading(false);
    }
  };

  const commonProps = {
    className:
      'w-full button button-lg max-sm:button-md button-secondary mt-10 h-[54px] text-lg max-sm:text-[14px] gap-3',
  };

  if (!isAuthed) {
    return (
      <button {...commonProps} disabled={isLoadingUser} onClick={authModal.open}>
        {isLoadingUser && <Spinner size="sm" color="light" />}
        Sign In
      </button>
    );
  }

  if (!userWallet?.attributes?.address) {
    return (
      <NextLink
        {...commonProps}
        href={nextProjectUrl.store('/profile/settings')}
        className={classNames(commonProps.className, {
          'button-disabled': isLoading,
        })}
      >
        Add EVM Wallet in Magic ID
      </NextLink>
    );
  }

  if (!isConnectedToSelfWallet) {
    return (
      <button {...commonProps} disabled={isLoading} onClick={handleConnect}>
        {isLoading && <Spinner size="sm" color="light" />}
        Connect with {truncate(userWallet.attributes.address)}
      </button>
    );
  }

  if (!isEnoughAllowance && !amount) {
    return (
      <button {...commonProps} disabled={true} onClick={() => {}}>
        Enter amount to swap
      </button>
    );
  }

  if (!isEnoughAllowance) {
    return (
      <button
        {...commonProps}
        disabled={isLoading || !fromToken || !toToken || !amount || balanceExceeded}
        onClick={() => handlePermissionCheck()}
      >
        {isLoading && <Spinner size="sm" color="light" />}
        Give permission to swap {fromToken?.attributes.symbol}
        <BaseInfoTooltip
          iconClassName="!icon !text-neutral-50"
          content={`To continue, you need to allow 1inch smart contracts to use your ${fromToken?.attributes.symbol}. This has to be done only once for each token.`}
        ></BaseInfoTooltip>
      </button>
    );
  }

  return (
    <button {...commonProps} disabled={!isAbleToSwap || isLoading} onClick={handleSwap}>
      {isLoading && <Spinner size="sm" color="light" />}
      Swap
    </button>
  );
};

export default MagicSwapButton;
